import { useTranslation } from "react-i18next";
import { CheckCircle2, Bell, Sparkles, Wallet, Users } from "lucide-react";
import StoreBadges from "@/components/brand/StoreBadges";
import StartFreeButton from "@/components/sts/StartFreeButton";

const PLANS = [
  { id: "free", key: "free", monthly: "$0", icon: Sparkles },
  { id: "pro", key: "premium", monthly: "$4.99", yearly: "$39.00", icon: Wallet },
  { id: "family", key: "family", monthly: "$9.99", yearly: "$79.00", icon: Users, highlighted: true },
];

function PlanCard({ plan, t }) {
  const p = t(`sts.pricing.plans.${plan.key}`, { returnObjects: true });
  const Icon = plan.icon;
  const free = plan.id === "free";
  return (
    <div
      data-testid={`sts-plan-${plan.id}`}
      className={`relative rounded-2xl border p-7 bg-white ${
        plan.highlighted ? "border-[#00C48C] shadow-xl ring-2 ring-[#00C48C]/15" : "border-slate-200 af-shadow-card"
      }`}
    >
      {plan.highlighted && (
        <span className="absolute -top-2.5 left-6 rounded-full bg-[#00C48C] text-white text-[10.5px] font-semibold uppercase tracking-wider px-2.5 py-0.5">
          {t("sts.pricing.bestValue")}
        </span>
      )}
      <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[#0A1128] text-white">
        <Icon className="h-5 w-5" />
      </div>
      <h3 data-testid={`sts-plan-${plan.id}-name`} className="mt-4 font-display text-[24px] text-[#0A1128]">{p.name}</h3>
      <p className="mt-1 text-[13.5px] text-slate-600">{p.tagline}</p>
      <div className="mt-6 flex items-baseline gap-2">
        <p className="font-display text-[42px] leading-none text-[#0A1128]">{plan.monthly}</p>
        <p className="text-[13px] text-slate-500">{t("sts.pricing.perMonth")}</p>
      </div>
      {free ? (
        <p className="mt-2 text-[13px] text-[#008466] font-semibold">{t("sts.pricing.freeForever")}</p>
      ) : (
        <>
          <p className="mt-2 text-[13px] text-slate-600">
            {t("sts.pricing.or")} <strong className="text-[#0A1128]">{plan.yearly}</strong> {t("sts.pricing.perYear")}{" "}
            <span className="text-[#008466] font-semibold">({p.yearlySave})</span>
          </p>
          <p className="text-[11.5px] text-slate-400">{p.breakEven}</p>
        </>
      )}
      <ul className="mt-6 space-y-2.5">
        {p.perks.map((perk) => (
          <li key={perk} className="flex items-start gap-2 text-[13.5px] text-slate-700 leading-snug">
            <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#00C48C] shrink-0" />
            <span>{perk}</span>
          </li>
        ))}
      </ul>
      {free && <StartFreeButton />}
      <div className={`${free ? "mt-4" : "mt-7"} flex justify-center`}>
        <StoreBadges product={`sts-plan-${plan.id}`} align="center" />
      </div>
    </div>
  );
}

export default function PlanCards() {
  const { t } = useTranslation("products");
  return (
    <>
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("sts.pricing.eyebrow")}</p>
        <h2 data-testid="sts-pricing-title" className="font-display mt-3 text-[36px] md:text-[44px] leading-[1.05] text-[#0A1128]">
          {t("sts.pricing.title")}
        </h2>
        <p className="mt-5 text-[15px] text-slate-600 leading-relaxed">{t("sts.pricing.subtitle")}</p>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#00C48C]/10 text-[#008466] px-4 py-1.5 text-[12.5px] font-semibold">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          {t("sts.pricing.badge")}
        </div>
        <p data-testid="sts-trial-reminder-note" className="mt-4 flex items-center justify-center gap-2 text-[13px] text-slate-600">
          <Bell className="h-3.5 w-3.5 text-[#00A373] shrink-0" />
          {t("sts.pricing.reminder")}
        </p>
      </div>
      <div className="mt-14 grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {PLANS.map((plan) => (
          <PlanCard key={plan.id} plan={plan} t={t} />
        ))}
      </div>
    </>
  );
}
